import {
  ColorVariant,
  ComponentAlignment,
  CornerRadius,
  MapComponent,
  MarkerVariant,
  MaterialIcon,
} from '@makyoapp/makyo-tw-ui'

import { GOOGLE_MAP_API_KEY } from '@/shared/gapi'
import { cleanUpText, generateMarkersHash } from '@/shared/utils'

import { LayoutDemo } from './LayoutDemo'

const zoomMapping: Record<string, number> = {
  far: 5,
  medium: 10,
  near: 15,
}

const settings = {
  latitude: 37.8199109,
  longitude: -122.4785598,
  avatar: 'https://storage.makyo.me/assets/defaultmap_avatar.jpg',
  description:
    'The Golden Gate Bridge is a suspension bridge spanning the Golden Gate, the one-mile-wide (1.6 km) strait connecting San Francisco Bay and the Pacific Ocean in California, United States.',
  draggable: true,
  fullscreenControl: true,
  title: 'Golden Gate Bridge',
  subtitle: 'RG9C+XH Presidio of San Francisco, San Francisco, CA',
  icon: 'Place',
  image: 'https://storage.makyo.me/assets/defaultmap_background.jpg',
  mapTypeControl: true,
  streetViewControl: true,
  zoomControl: true,
  zoomLevel: 'near',
  info: 'default',
  width: '100%',
  height: 400,
  alignment: 'parent',
  styles: {
    '--md-sys-color-background': '#fcfdf6',
    '--md-sys-color-error': '#ba1a1a',
    '--md-sys-color-error-container': '#ffdad6',
    '--md-sys-color-inverse-on-surface': '#f1f1eb',
    '--md-sys-color-inverse-primary': '#54e252',
    '--md-sys-color-inverse-surface': '#2f312d',
    '--md-sys-color-on-background': '#1a1c19',
    '--md-sys-color-on-error': '#ffffff',
    '--md-sys-color-on-error-container': '#410002',
    '--md-sys-color-on-primary': '#ffffff',
    '--md-sys-color-on-primary-container': '#002202',
    '--md-sys-color-on-primary-fixed': '#002202',
    '--md-sys-color-on-primary-fixed-variant': '#00530a',
    '--md-sys-color-on-secondary': '#ffffff',
    '--md-sys-color-on-secondary-container': '#111f0f',
    '--md-sys-color-on-secondary-fixed': '#111f0f',
    '--md-sys-color-on-secondary-fixed-variant': '#3c4b38',
    '--md-sys-color-on-surface': '#1a1c19',
    '--md-sys-color-on-surface-variant': '#42493f',
    '--md-sys-color-on-tertiary': '#ffffff',
    '--md-sys-color-on-tertiary-container': '#002022',
    '--md-sys-color-on-tertiary-fixed': '#002022',
    '--md-sys-color-on-tertiary-fixed-variant': '#1e4d51',
    '--md-sys-color-outline': '#73796e',
    '--md-sys-color-outline-variant': '#c2c8bc',
    '--md-sys-color-primary': '#006e11',
    '--md-sys-color-primary-container': '#74ff6c',
    '--md-sys-color-primary-fixed': '#74ff6c',
    '--md-sys-color-primary-fixed-dim': '#54e252',
    '--md-sys-color-primary-surface': '#d6ffc9',
    '--md-sys-color-scrim': '#000000',
    '--md-sys-color-secondary': '#53634e',
    '--md-sys-color-secondary-container': '#d6e8ce',
    '--md-sys-color-secondary-fixed': '#d6e8ce',
    '--md-sys-color-secondary-fixed-dim': '#baccb2',
    '--md-sys-color-secondary-surface': '#e7f9de',
    '--md-sys-color-shadow': '#000000',
    '--md-sys-color-surface': '#fcfdf6',
    '--md-sys-color-surface-bright': '#f9faf3',
    '--md-sys-color-surface-container': '#eeeee8',
    '--md-sys-color-surface-container-high': '#e8e9e2',
    '--md-sys-color-surface-container-highest': '#e2e3dd',
    '--md-sys-color-surface-container-low': '#f4f4ee',
    '--md-sys-color-surface-container-lowest': '#ffffff',
    '--md-sys-color-surface-dim': '#dadad4',
    '--md-sys-color-surface-tint': '#006e11',
    '--md-sys-color-surface-variant': '#dee4d8',
    '--md-sys-color-tertiary': '#386569',
    '--md-sys-color-tertiary-container': '#bcebef',
    '--md-sys-color-tertiary-fixed': '#bcebef',
    '--md-sys-color-tertiary-fixed-dim': '#a0cfd3',
    '--md-sys-color-tertiary-surface': '#d2fbff',
    '--md-sys-color-custom-extended-1-color': '#5e5e5e',
    '--md-sys-color-custom-extended-1-source': '#000000',
    '--md-sys-color-custom-extended-1-on-color': '#ffffff',
    '--md-sys-color-custom-extended-1-color-container': '#e2e2e2',
    '--md-sys-color-custom-extended-1-on-color-container': '#1b1b1b',
    '--md-sys-color-custom-extended-2-color': '#5e5e5e',
    '--md-sys-color-custom-extended-2-source': '#000000',
    '--md-sys-color-custom-extended-2-on-color': '#ffffff',
    '--md-sys-color-custom-extended-2-color-container': '#e2e2e2',
    '--md-sys-color-custom-extended-2-on-color-container': '#1b1b1b',
    '--md-sys-color-custom-extended-3-color': '#5d5f5f',
    '--md-sys-color-custom-extended-3-source': '#ffffff',
    '--md-sys-color-custom-extended-3-on-color': '#ffffff',
    '--md-sys-color-custom-extended-3-color-container': '#e2e2e2',
    '--md-sys-color-custom-extended-3-on-color-container': '#1a1c1c',
    '--md-sys-color-custom-extended-4-color': '#5d5f5f',
    '--md-sys-color-custom-extended-4-source': '#ffffff',
    '--md-sys-color-custom-extended-4-on-color': '#ffffff',
    '--md-sys-color-custom-extended-4-color-container': '#e2e2e2',
    '--md-sys-color-custom-extended-4-on-color-container': '#1a1c1c',
    '--md-sys-color-custom-extended-5-color': '#5e5f5c',
    '--md-sys-color-custom-extended-5-source': '#faf9f5',
    '--md-sys-color-custom-extended-5-on-color': '#ffffff',
    '--md-sys-color-custom-extended-5-color-container': '#e3e2df',
    '--md-sys-color-custom-extended-5-on-color-container': '#1b1c1a',
    '--md-sys-color-custom-extended-6-color': '#5e5f58',
    '--md-sys-color-custom-extended-6-source': '#efefe6',
    '--md-sys-color-custom-extended-6-on-color': '#ffffff',
    '--md-sys-color-custom-extended-6-color-container': '#e3e3da',
    '--md-sys-color-custom-extended-6-on-color-container': '#1a1c17',
    '--md-sys-color-custom-extended-7-color': '#c00000',
    '--md-sys-color-custom-extended-7-source': '#cc0000',
    '--md-sys-color-custom-extended-7-on-color': '#ffffff',
    '--md-sys-color-custom-extended-7-color-container': '#ffdad4',
    '--md-sys-color-custom-extended-7-on-color-container': '#410000',
    '--md-sys-color-custom-extended-8-color': '#a33f00',
    '--md-sys-color-custom-extended-8-source': '#ad4300',
    '--md-sys-color-custom-extended-8-on-color': '#ffffff',
    '--md-sys-color-custom-extended-8-color-container': '#ffdbcd',
    '--md-sys-color-custom-extended-8-on-color-container': '#360f00',
  },
  theme: 'light',
  shape: 'none',
  margin: {
    bottom: {
      unit: 'px',
      value: 0,
    },
    left: {
      unit: 'px',
      value: 0,
    },
    right: {
      unit: 'px',
      value: 0,
    },
    top: {
      unit: 'px',
      value: 0,
    },
  },
  clickEvents: [
    {
      settings: {
        value: 'https://maps.app.goo.gl/ibxjktgVHAuAFb347',
      },
      type: 'open_open-link',
    },
  ],
  colorVariant: 'primary-90',
  colorMarker: 'primary-90',
}

export const LocationDemo = () => {
  const zoom = zoomMapping[settings.zoomLevel] || 15

  const { shape, margin } = settings

  const lat = settings?.latitude || -6.4245187
  const lng = settings?.longitude || 106.8032919
  const colorMarker = settings?.colorMarker || 'primary-90'
  const getCustomElement = () => {
    if (settings.icon) {
      return (
        <div
          className={`bg-${colorMarker} rounded-full shadow-lg flex items-center justify-center cursor-pointer text-on-${colorMarker} p-2`}
        >
          <MaterialIcon icon={settings.icon} size={36} />
        </div>
      )
    }

    if (settings.image) {
      const MarkersIcon = () => (
        <svg width='32' height='40' viewBox='0 0 32 40' xmlns='http://www.w3.org/2000/svg'>
          <path d='M16.0044 19.5C16.9681 19.5 17.7917 19.1569 18.475 18.4706C19.1583 17.7843 19.5 16.9593 19.5 15.9956C19.5 15.0319 19.1569 14.2083 18.4706 13.525C17.7843 12.8417 16.9593 12.5 15.9956 12.5C15.0319 12.5 14.2083 12.8431 13.525 13.5294C12.8417 14.2157 12.5 15.0407 12.5 16.0044C12.5 16.9681 12.8431 17.7917 13.5294 18.475C14.2157 19.1583 15.0407 19.5 16.0044 19.5ZM16 40C10.6333 35.4333 6.625 31.1917 3.975 27.275C1.325 23.3583 0 19.7333 0 16.4C0 11.4 1.60833 7.41667 4.825 4.45C8.04167 1.48333 11.7667 0 16 0C20.2333 0 23.9583 1.48333 27.175 4.45C30.3917 7.41667 32 11.4 32 16.4C32 19.7333 30.675 23.3583 28.025 27.275C25.375 31.1917 21.3667 35.4333 16 40Z'></path>
        </svg>
      )

      // Custom Marker Components
      const Image = () => (
        <div
          className={`w-9 h-9 rounded-full shadow-lg flex items-center justify-center cursor-pointer`}
          style={{ color: `var(--color-${colorMarker})` }}
        >
          <MarkersIcon />
          <div className='absolute top-0.5 left-1/2 transform -translate-x-1/2 '>
            <img src={settings.image} className='w-6 h-6 rounded-full' />
          </div>
        </div>
      )
      return <Image />
    }

    return undefined
  }

  const handleItemClick = () => {
    console.log('clicked')
  }

  const marker = {
    position: {
      lat,
      lng,
    },
    icon: settings.icon,
    avatar: settings.avatar,
    draggable: settings.draggable,
    info: {
      variant: settings.info as MarkerVariant,
      header: cleanUpText(settings.title),
      subHeader: cleanUpText(settings.subtitle),
      description: cleanUpText(settings.description),
      ...(settings.image && {
        image: {
          src: settings.image,
          alt: settings.title,
        },
      }),
      ...(settings.avatar && {
        avatar: {
          src: settings.avatar,
          alt: settings.title,
        },
      }),
    },
    onPopoverClick: handleItemClick,
    ...(getCustomElement() && { customElement: getCustomElement() }),
  }

  const clickEventsKey = ''

  return (
    <LayoutDemo title='Map' bgColor='var(--color-primary-90)'>
      <div className='h-full w-full overflow-hidden rounded-md'>
        <MapComponent
          margin={margin}
          width={'100%'}
          cornerRadius={shape as CornerRadius}
          height={'100%'}
          alignment={settings.alignment as ComponentAlignment}
          key={[
            generateMarkersHash(settings),
            lat,
            lng,
            colorMarker,
            settings.icon,
            clickEventsKey,
            settings.zoomLevel,
            settings.fullscreenControl,
            settings.streetViewControl,
            settings.mapTypeControl,
            settings.theme,
            zoom,
            settings.zoomControl,
          ].join('-')}
          apiKey={GOOGLE_MAP_API_KEY}
          colorVariant={settings.colorVariant as ColorVariant}
          colorMarker={settings.colorMarker as ColorVariant}
          mapTypeControl={settings.mapTypeControl}
          zoomControl={settings.zoomControl}
          streetViewControl={settings.streetViewControl}
          fullscreenControl={settings.fullscreenControl}
          zoom={zoom}
          clustered={false}
          theme={settings.theme as 'light' | 'dark' | undefined}
          markers={[marker]}
        />
      </div>
    </LayoutDemo>
  )
}
